import { unifiedApi } from "@/common";
import { ISubscription } from "@/common/api/unifiedPortal/interfaces";
import { LAST_API_FETCH_INTERVAL } from "@/define";
import { logEvent } from "@/common/logger";
import { defineStore } from "pinia";
import { handleApiError } from "@/common/handleApiError";
import { AxiosError } from "axios";
import { ref } from "vue";

export const useSubscriptionsStore = defineStore("subscriptions", () => {
  const subscriptions = ref<ISubscription[]>([]);
  const lastRequestTime = ref(0);
  const isRunning = ref(false);

  async function getSubscriptions() {
    try {
      // Waits for the current call to finish before trying and get aborted (in case a simultaneous call)
      while (isRunning.value) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      const currentTime = new Date().getTime();
      if (currentTime - lastRequestTime.value >= LAST_API_FETCH_INTERVAL) {
        lastRequestTime.value = new Date().getTime();
        isRunning.value = true;
        subscriptions.value = (await unifiedApi.getSubscriptions()).data;
        isRunning.value = false;
        logEvent("subscriptions state updated", "subscriptions store", subscriptions);
        return subscriptions.value;
      }
      return subscriptions.value;
    } catch (err) {
      //redirect to error page
      handleApiError(err as AxiosError, false);
      // return existing state to satisfy places it is called even though it will be redirected
      return subscriptions.value;
    }
  }

  //This should replace get subscriptions going forward.
  //Separating the call to get the data and the return of the data is easier to unit test
  //We may want to refactor getSubscriptions calls later
  async function populateSubscriptions() {
    try {
      // Waits for the current call to finish before trying and get aborted (in case a simultaneous call)
      while (isRunning.value) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      const currentTime = new Date().getTime();
      if (currentTime - lastRequestTime.value >= LAST_API_FETCH_INTERVAL) {
        lastRequestTime.value = new Date().getTime();
        isRunning.value = true;
        subscriptions.value = (await unifiedApi.getSubscriptions()).data;
        isRunning.value = false;
        logEvent("subscriptions state updated", "subscriptions store", subscriptions);
      }
    } catch (err) {
      //redirect to error page
      handleApiError(err as AxiosError, false);
    }
  }

  //If the user has updated their subscriptions off site we want to refresh the store next time they come to the page
  function forceRefresh() {
    lastRequestTime.value = new Date().getTime() - LAST_API_FETCH_INTERVAL;
    logEvent("Force Subscriptions store refresh");
  }

  async function forceRefreshAndGetSubscriptions() {
    forceRefresh();
    return await getSubscriptions();
  }

  return {
    subscriptions,
    lastRequestTime,
    isRunning,
    getSubscriptions,
    forceRefresh,
    forceRefreshAndGetSubscriptions,
    populateSubscriptions,
  };
});
